import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Dependent } from "@/shared/types/family";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { Button } from "@/shared/components/button";
import { AddFamilyMemberModal } from "./AddFamilyMemberModal";
import { EditFamilyMemberModal } from "./EditFamilyMemberModal";

interface ManageFamilySectionProps {
  dependents: Dependent[];
  /** Refetches the dependents list from the backend — called after a successful add. */
  onChange: () => void;
}

function getInitials(name: string): string {
  const parts = name.split(" ").filter(Boolean);
  return parts.slice(0, 2).map((p) => p.charAt(0)).join("").toUpperCase();
}

const ACCESS_LEVEL_LABEL: Record<Dependent["accessLevel"], string> = {
  full: "Full Access",
  limited: "Limited Access",
  "view-only": "View Only",
};

interface DependentCardProps {
  dependent: Dependent;
  onEdit: () => void;
  onViewDashboard: () => void;
}

function DependentCard({ dependent, onEdit, onViewDashboard }: DependentCardProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all hover:border-[#F36F8F]/50 hover:shadow-md">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#111827] text-sm font-bold !text-white">
          {getInitials(dependent.name)}
        </div>
        <div>
          <h3 className="text-sm font-extrabold text-[#0A2947]">{dependent.name}</h3>
          <p className="mt-0.5 text-xs text-[#6B7280]">
            {dependent.relationship} · Age {dependent.age}
          </p>
          <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[#DCFCE7] px-2.5 py-0.5 text-[11px] font-semibold text-[#16A34A]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
            {ACCESS_LEVEL_LABEL[dependent.accessLevel]}
          </span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="outline"
          onClick={onEdit}
          className="!flex !items-center !gap-1.5 !rounded-lg !border-[#E5E7EB] !px-3.5 !py-2 !text-xs !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!text-[#0A2947]"
        >
          <Icon icon="lucide:pencil" className="h-3.5 w-3.5" />
          Edit Profile
        </Button>
        <Button
          onClick={onViewDashboard}
          className="!flex !items-center !gap-1.5 !rounded-lg !bg-[#E4002B]/50 !px-3.5 !py-2 !text-xs !font-semibold !text-white hover:!bg-[#E4002B]/60"
        >
          <Icon icon="lucide:external-link" className="h-3.5 w-3.5 text-white" />
          View Dashboard
        </Button>
      </div>
    </div>
  );
}

export function ManageFamilySection({ dependents, onChange }: ManageFamilySectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveDependentId } = useFamily();
  const shouldOpenAddModal = new URLSearchParams(location.search).get("add-family-member") === "1";
  const [showAddFamilyMember, setShowAddFamilyMember] = useState(shouldOpenAddModal);
  const [editingDependentId, setEditingDependentId] = useState<string | null>(null);
  const editingDependent = dependents.find((d) => d.id === editingDependentId) ?? null;

  useEffect(() => {
    if (shouldOpenAddModal) setShowAddFamilyMember(true);
  }, [shouldOpenAddModal]);

  const handleAddModalOpenChange = (open: boolean) => {
    setShowAddFamilyMember(open);
    if (!open && shouldOpenAddModal) {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleViewDashboard = (dependent: Dependent) => {
    setActiveDependentId(dependent.id);
    navigate("/patient");
  };

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-extrabold text-[#0A2947]">Dependents You Manage</h2>
        <Button
          onClick={() => setShowAddFamilyMember(true)}
          className="!flex !items-center !gap-2 !rounded-lg !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60"
        >
          <Icon icon="lucide:plus" className="h-4 w-4 text-white" />
          Add Family Member
        </Button>
      </div>

      <div className="space-y-3">
        {dependents.map((dependent) => (
          <DependentCard
            key={dependent.id}
            dependent={dependent}
            onEdit={() => setEditingDependentId(dependent.id)}
            onViewDashboard={() => handleViewDashboard(dependent)}
          />
        ))}
      </div>

      {dependents.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-[#E5E7EB] bg-white py-16 text-center shadow-sm">
          <p className="text-sm font-extrabold text-[#0A2947]">No dependents added yet</p>
          <p className="mt-1 text-xs font-semibold text-[#9CA3AF]">Add a family member to start managing their records</p>
        </div>
      )}

      <AddFamilyMemberModal open={showAddFamilyMember} onOpenChange={handleAddModalOpenChange} onCreated={onChange} />
      {editingDependent && (
        <EditFamilyMemberModal
          open={editingDependentId !== null}
          onOpenChange={(open) => setEditingDependentId(open ? editingDependentId : null)}
          dependent={editingDependent}
          onSaved={onChange}
        />
      )}
    </div>
  );
}
